/**
 * WearableHealthCard — 小米 Watch S5 健康数据卡片
 * 数据项：心电图(ECG)波形、心率、血压(收缩压/舒张压)、血氧、睡眠、步数、电量
 *
 * 设计语言：xiaofeng-os 暗色玻璃态 + 医疗健康紫金配色
 * 动效：ECG 实时波形扫描线、心率环形脉冲、数字过渡动画
 * ECG 主题：5 套配色方案，localStorage 持久化切换
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion } from 'motion/react';
import {
  Heart, Footprints, Moon, Droplets, Battery, Watch, Wifi, WifiOff,
  Activity, TrendingUp, TrendingDown, Zap
} from 'lucide-react';

// ── Types ──

export interface WearableHealthData {
  deviceName: string;
  online: boolean;
  heartRate: number;          // bpm
  ecgWaveform: number[];      // 心电图波形采样点 [-1, 1]
  bloodPressureSystolic: number;   // 收缩压 mmHg
  bloodPressureDiastolic: number;  // 舒张压 mmHg
  spo2: number;               // 血氧 %
  sleepHours: number;         // 睡眠 小时
  steps: number;              // 步数
  battery: number;            // 电量 %
  lastUpdated?: string;
}

// ── ECG Themes ──

export interface EcgTheme {
  id: string;
  name: string;
  nameEn: string;
  color: string;          // 波形主色
  gridColor: string;      // 网格线色
  bgAccent: string;       // 背景强调色
  dotColor: string;       // 扫描点色
  label: string;          // 按钮圆点色（Tailwind class）
}

export const ECG_THEMES: EcgTheme[] = [
  {
    id: 'neon-green',
    name: '霓虹绿',
    nameEn: 'Neon Green',
    color: 'oklch(72% 0.22 160)',
    gridColor: 'oklch(72% 0.22 160 / 0.15)',
    bgAccent: 'oklch(72% 0.22 160 / 0.04)',
    dotColor: 'oklch(72% 0.22 160)',
    label: 'bg-green-400',
  },
  {
    id: 'medical-purple',
    name: '医疗紫',
    nameEn: 'Medical Purple',
    color: 'oklch(65% 0.2 300)',
    gridColor: 'oklch(65% 0.2 300 / 0.15)',
    bgAccent: 'oklch(65% 0.2 300 / 0.04)',
    dotColor: 'oklch(65% 0.2 300)',
    label: 'bg-purple-400',
  },
  {
    id: 'cyan-medical',
    name: '青蓝监护',
    nameEn: 'Cyan Monitor',
    color: 'oklch(68% 0.16 200)',
    gridColor: 'oklch(68% 0.16 200 / 0.15)',
    bgAccent: 'oklch(68% 0.16 200 / 0.04)',
    dotColor: 'oklch(68% 0.16 200)',
    label: 'bg-cyan-400',
  },
  {
    id: 'amber-vital',
    name: '琥珀体征',
    nameEn: 'Amber Vital',
    color: 'oklch(68% 0.16 70)',
    gridColor: 'oklch(68% 0.16 70 / 0.15)',
    bgAccent: 'oklch(68% 0.16 70 / 0.04)',
    dotColor: 'oklch(68% 0.16 70)',
    label: 'bg-amber-400',
  },
  {
    id: 'rose-pulse',
    name: '玫红脉动',
    nameEn: 'Rose Pulse',
    color: 'oklch(62% 0.18 10)',
    gridColor: 'oklch(62% 0.18 10 / 0.15)',
    bgAccent: 'oklch(62% 0.18 10 / 0.04)',
    dotColor: 'oklch(62% 0.18 10)',
    label: 'bg-rose-400',
  },
];

const ECG_THEME_STORAGE_KEY = 'xf_ecg_theme';

// ── ECG Waveform Animation ──

function EcgWaveform({ waveform, theme }: { waveform: number[]; theme: EcgTheme }) {
  const color = theme.color;
  const svgRef = useRef<SVGSVGElement>(null);
  const [progress, setProgress] = useState(0);
  const width = 260;
  const height = 80;
  const paddingX = 4;
  const paddingY = 10;
  const drawWidth = width - paddingX * 2;
  const drawHeight = height - paddingY * 2;

  // 扫描线动画
  useEffect(() => {
    let raf: number;
    let start: number | null = null;
    const duration = 3000; // 3秒扫一遍

    const animate = (ts: number) => {
      if (!start) start = ts;
      const elapsed = (ts - start) % duration;
      setProgress(elapsed / duration);
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, []);

  // 生成 SVG path
  const pathD = useMemo(() => {
    if (waveform.length < 2) return '';
    const stepX = drawWidth / (waveform.length - 1);
    const midY = paddingY + drawHeight / 2;
    const amp = drawHeight / 2.5;

    return waveform.map((v, i) => {
      const x = paddingX + i * stepX;
      const y = midY - v * amp;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    }).join(' ');
  }, [waveform]);

  // 扫描线位置
  const scanX = paddingX + progress * drawWidth;

  return (
    <div className="relative w-full flex justify-center" style={{ height }}>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="overflow-visible"
      >
        {/* Grid lines */}
        <defs>
          <linearGradient id="ecgGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={color} stopOpacity="0.1" />
            <stop offset="50%" stopColor={color} stopOpacity="0.6" />
            <stop offset="100%" stopColor={color} stopOpacity="0.1" />
          </linearGradient>
          <linearGradient id="ecgScanGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="transparent" />
            <stop offset="40%" stopColor={color} stopOpacity="0" />
            <stop offset="48%" stopColor={color} stopOpacity="0.9" />
            <stop offset="52%" stopColor={color} stopOpacity="0.9" />
            <stop offset="60%" stopColor={color} stopOpacity="0" />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>
        </defs>

        {/* Horizontal grid */}
        {[0.25, 0.5, 0.75].map(r => (
          <line
            key={r}
            x1={paddingX}
            y1={paddingY + drawHeight * r}
            x2={width - paddingX}
            y2={paddingY + drawHeight * r}
            stroke={theme.gridColor}
            strokeWidth="0.5"
          />
        ))}

        {/* Waveform line */}
        <path
          d={pathD}
          fill="none"
          stroke="url(#ecgGrad)"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Scanning glow overlay */}
        <rect
          x={scanX - 20}
          y={paddingY}
          width={40}
          height={drawHeight}
          fill="url(#ecgScanGrad)"
          opacity="0.8"
        />

        {/* Scan line */}
        <line
          x1={scanX}
          y1={paddingY}
          x2={scanX}
          y2={paddingY + drawHeight}
          stroke={color}
          strokeWidth="1"
          opacity="0.7"
        />
      </svg>

      {/* Blinking dot at scan position */}
      <motion.div
        className="absolute top-1/2 w-1.5 h-1.5 rounded-full"
        style={{
          left: `calc(50% - ${width / 2}px + ${scanX}px - 3px)`,
          backgroundColor: color,
          boxShadow: `0 0 8px ${color}`,
        }}
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 0.6, repeat: Infinity, ease: 'easeInOut' }}
      />
    </div>
  );
}

// ── Heart Rate Ring ──

function HeartRing({ rate, size = 100 }: { rate: number; size?: number }) {
  const strokeW = 6;
  const radius = (size - strokeW) / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;

  const minR = 40, maxR = 180;
  const pct = Math.max(0, Math.min(1, (rate - minR) / (maxR - minR)));
  const dashOffset = circumference * (1 - pct);

  const color =
    rate < 60  ? 'oklch(65% 0.14 240)' :
    rate < 100 ? 'oklch(65% 0.16 160)' :
    rate < 120 ? 'oklch(65% 0.16 60)'  :
                 'oklch(60% 0.16 20)';

  const glowColor = color.replace(')', ' / 0.35)').replace('oklch(', 'oklch(');

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="absolute inset-0"
        style={{ filter: `drop-shadow(0 0 10px ${glowColor})` }}>
        <circle cx={center} cy={center} r={radius} fill="none"
          stroke="oklch(100% 0 0 / 0.06)" strokeWidth={strokeW} />
        <motion.circle cx={center} cy={center} r={radius} fill="none"
          stroke={color} strokeWidth={strokeW} strokeLinecap="round"
          strokeDasharray={circumference}
          animate={{ strokeDashoffset: dashOffset }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          transform={`rotate(-90 ${center} ${center})`} />
      </svg>

      {/* Pulse ring */}
      <motion.div className="absolute rounded-full border"
        style={{
          width: size * 0.85, height: size * 0.85,
          borderColor: color, borderWidth: 1, opacity: 0.3,
        }}
        animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0, 0.3] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="absolute flex flex-col items-center">
        <motion.span className="text-2xl font-bold tracking-tighter" style={{ color }}
          key={rate} initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.35, ease: 'easeOut' }}>
          {rate}
        </motion.span>
        <span className="text-[10px] text-white/25 mt-0.5">BPM</span>
      </div>
    </div>
  );
}

// ── Metric Item ──

function MetricItem({
  icon, value, unit, label, color = 'text-white/60', trend,
}: {
  icon: React.ReactNode;
  value: string | number;
  unit?: string;
  label: string;
  color?: string;
  trend?: 'up' | 'down' | null;
}) {
  return (
    <div className="flex flex-col items-center gap-0.5 p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.04]
                    hover:bg-white/[0.04] hover:border-white/[0.07] transition-all duration-300 group">
      <div className="flex items-center gap-1 mb-0.5">
        <span className="opacity-40 group-hover:opacity-60 transition-opacity">{icon}</span>
        {trend && (
          <span className="opacity-0 group-hover:opacity-100 transition-opacity">
            {trend === 'up'
              ? <TrendingUp size={9} className="text-green-400/50" />
              : <TrendingDown size={9} className="text-red-400/50" />}
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-0.5">
        <motion.span className={`text-base font-bold tracking-tight ${color}`}
          key={String(value)}
          initial={{ opacity: 0, y: 3 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}>
          {value}
        </motion.span>
        {unit && <span className="text-[10px] text-white/25">{unit}</span>}
      </div>
      <span className="text-[9px] text-white/20 font-medium uppercase tracking-wider">{label}</span>
    </div>
  );
}

// ── Blood Pressure Bar ──

function BPBar({ systolic, diastolic }: { systolic: number; diastolic: number }) {
  const max = 200;
  const sysPct = Math.min(1, systolic / max);
  const diaPct = Math.min(1, diastolic / max);

  const sysColor = systolic > 140 ? 'oklch(60% 0.14 20)' :
    systolic > 120 ? 'oklch(65% 0.14 60)' : 'oklch(65% 0.14 160)';
  const diaColor = diastolic > 90 ? 'oklch(60% 0.14 20)' :
    diastolic > 80 ? 'oklch(65% 0.14 60)' : 'oklch(65% 0.14 160)';

  return (
    <div className="flex flex-col items-center gap-1.5 w-full">
      {/* Systolic */}
      <div className="flex items-center gap-2 w-full">
        <span className="text-[9px] text-white/25 w-12 text-right shrink-0">收缩压</span>
        <div className="flex-1 h-2 bg-white/[0.05] rounded-full overflow-hidden">
          <motion.div className="h-full rounded-full"
            style={{ backgroundColor: sysColor }}
            animate={{ width: `${sysPct * 100}%` }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} />
        </div>
        <span className="text-[10px] font-bold w-8 text-right" style={{ color: sysColor }}>{systolic}</span>
      </div>
      {/* Diastolic */}
      <div className="flex items-center gap-2 w-full">
        <span className="text-[9px] text-white/25 w-12 text-right shrink-0">舒张压</span>
        <div className="flex-1 h-2 bg-white/[0.05] rounded-full overflow-hidden">
          <motion.div className="h-full rounded-full"
            style={{ backgroundColor: diaColor }}
            animate={{ width: `${diaPct * 100}%` }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} />
        </div>
        <span className="text-[10px] font-bold w-8 text-right" style={{ color: diaColor }}>{diastolic}</span>
      </div>
    </div>
  );
}

// ── Main Card ──

interface WearableHealthCardProps {
  data: WearableHealthData;
}

export function WearableHealthCard({ data }: WearableHealthCardProps) {
  const {
    deviceName, online, heartRate, ecgWaveform,
    bloodPressureSystolic, bloodPressureDiastolic,
    spo2, sleepHours, steps, battery, lastUpdated,
  } = data;

  // ── ECG Theme ──
  const [ecgThemeId, setEcgThemeId] = useState<string>(() => {
    try { return localStorage.getItem(ECG_THEME_STORAGE_KEY) || ECG_THEMES[0].id; }
    catch { return ECG_THEMES[0].id; }
  });
  const ecgTheme = ECG_THEMES.find(t => t.id === ecgThemeId) || ECG_THEMES[0];

  const switchTheme = (id: string) => {
    setEcgThemeId(id);
    localStorage.setItem(ECG_THEME_STORAGE_KEY, id);
  };

  const stepsStr = steps >= 10000 ? `${(steps / 10000).toFixed(1)}万` : steps.toLocaleString();
  const sleepH = Math.floor(sleepHours);
  const sleepM = Math.round((sleepHours - sleepH) * 60);
  const sleepStr = `${sleepH}h${sleepM > 0 ? sleepM + 'm' : ''}`;

  const spo2Color = spo2 >= 95 ? 'oklch(65% 0.14 160)' : spo2 >= 90 ? 'oklch(65% 0.14 60)' : 'oklch(60% 0.14 20)';
  const batteryColor = battery > 60 ? 'oklch(60% 0.14 160)' : battery > 20 ? 'oklch(60% 0.14 60)' : 'oklch(60% 0.14 20)';

  const updateTime = lastUpdated
    ? (() => { const d = new Date(lastUpdated); return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:${String(d.getSeconds()).padStart(2,'0')}`; })()
    : '--:--:--';

  return (
    <motion.div
      className="relative rounded-2xl border border-purple-500/[0.08] bg-gradient-to-br from-purple-500/[0.02] to-transparent
                 backdrop-blur-2xl p-4 transition-colors duration-300
                 hover:border-purple-500/[0.12] hover:from-purple-500/[0.04]"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* ── Top bar ── */}
      <div className="flex items-center justify-between mb-3 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="p-1.5 rounded-lg bg-purple-500/10">
            <Watch size={13} className="text-purple-400/70" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-[11px] font-bold text-white/50 truncate">{deviceName}</span>
            <span className="text-[9px] text-white/20 font-mono">Wear OS · HyperOS</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <div className={`w-1.5 h-1.5 rounded-full ${online ? 'bg-green-400 animate-pulse' : 'bg-red-400/50'}`} />
          <span className={`text-[9px] font-medium ${online ? 'text-green-400/50' : 'text-red-400/40'}`}>
            {online ? '已连接' : '离线'}
          </span>
        </div>
      </div>

      {/* ── ECG Waveform ── */}
      <div className="mb-3 px-1">
        <div className="flex items-center gap-2 mb-1.5">
          <Activity size={11} className="text-purple-400/50" />
          <span className="text-[9px] font-bold uppercase tracking-widest text-white/25">心电图 ECG</span>
          {/* Theme switcher dots */}
          <div className="flex items-center gap-1.5 ml-auto">
            {ECG_THEMES.map(t => (
              <button
                key={t.id}
                onClick={() => switchTheme(t.id)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${t.label} 
                  ${ecgThemeId === t.id
                    ? 'ring-2 ring-white/30 scale-125'
                    : 'opacity-40 hover:opacity-70 hover:scale-110'}`}
                title={`${t.name} / ${t.nameEn}`}
              />
            ))}
          </div>
        </div>
        <div className="rounded-xl bg-white/[0.02] border border-white/[0.04] py-2 overflow-hidden"
          style={{ backgroundColor: ecgTheme.bgAccent }}>
          <EcgWaveform waveform={ecgWaveform} theme={ecgTheme} />
        </div>
      </div>

      {/* ── Heart Rate + BP side by side ── */}
      <div className="flex items-stretch gap-4 mb-3 px-1">
        {/* Heart Ring */}
        <div className="flex-shrink-0 flex items-center">
          <HeartRing rate={heartRate} size={90} />
        </div>
        {/* Blood Pressure Bar */}
        <div className="flex-1 flex flex-col justify-center min-w-0">
          <div className="flex items-center gap-1.5 mb-2">
            <Zap size={10} className="text-amber-400/50" />
            <span className="text-[9px] font-bold uppercase tracking-widest text-white/25">血压</span>
            <span className="text-[8px] text-white/15 ml-auto">mmHg</span>
          </div>
          <BPBar systolic={bloodPressureSystolic} diastolic={bloodPressureDiastolic} />
        </div>
      </div>

      {/* ── Metrics Grid 2x3 ── */}
      <div className="grid grid-cols-3 gap-2">
        <MetricItem
          icon={<Droplets size={12} className="text-blue-400/40" />}
          value={spo2} unit="%" label="血氧" color={spo2Color}
        />
        <MetricItem
          icon={<Moon size={12} className="text-indigo-400/40" />}
          value={sleepStr} label="睡眠"
        />
        <MetricItem
          icon={<Footprints size={12} className="text-cyan-400/40" />}
          value={stepsStr} label="步数"
        />
        <MetricItem
          icon={<Heart size={12} className="text-pink-400/40" />}
          value={heartRate} unit="bpm" label="心率"
        />
        <MetricItem
          icon={<Activity size={12} className="text-purple-400/40" />}
          value={`${bloodPressureSystolic}/${bloodPressureDiastolic}`} label="血压"
        />
        <MetricItem
          icon={<Battery size={12} style={{ color: batteryColor }} />}
          value={battery} unit="%" label="电量" color={batteryColor}
        />
      </div>

      {/* ── Footer ── */}
      <div className="flex items-center justify-between mt-3 pt-2 border-t border-white/[0.04]">
        <span className="text-[8px] text-white/12 font-mono">更新于 {updateTime}</span>
        <div className="flex items-center gap-1">
          <div className={`w-1.5 h-1.5 rounded-full ${online ? 'bg-green-400/40' : 'bg-red-400/30'}`} />
          <span className="text-[8px] text-white/15">{online ? '实时同步中' : '连接断开'}</span>
        </div>
      </div>

      {/* Offline overlay */}
      {!online && (
        <div className="absolute inset-0 rounded-2xl bg-black/20 backdrop-blur-[2px] flex items-center justify-center z-20">
          <span className="text-xs font-bold text-white/30 tracking-wider uppercase">离线</span>
        </div>
      )}
    </motion.div>
  );
}

// ── ECG Waveform Generator ──

function generateEcgWaveform(length: number = 80): number[] {
  const result: number[] = [];
  const beatInterval = 18; // 每18个采样点一个心跳周期

  for (let i = 0; i < length; i++) {
    const phaseInBeat = (i % beatInterval) / beatInterval;

    let value = 0;

    if (phaseInBeat < 0.05) {
      // P wave (atrial depolarization)
      value = 0.1 * Math.sin(phaseInBeat / 0.05 * Math.PI);
    } else if (phaseInBeat < 0.15) {
      value = 0;
    } else if (phaseInBeat < 0.2) {
      // Q dip
      value = -0.08 * Math.sin((phaseInBeat - 0.15) / 0.05 * Math.PI);
    } else if (phaseInBeat < 0.25) {
      // R peak
      value = 0.9 * Math.sin((phaseInBeat - 0.2) / 0.05 * Math.PI);
    } else if (phaseInBeat < 0.3) {
      // S dip
      value = -0.3 * Math.sin((phaseInBeat - 0.25) / 0.05 * Math.PI);
    } else if (phaseInBeat < 0.45) {
      value = 0;
    } else if (phaseInBeat < 0.55) {
      // T wave
      value = 0.2 * Math.sin((phaseInBeat - 0.45) / 0.1 * Math.PI);
    } else {
      value = 0;
    }

    // Add slight noise
    value += (Math.random() - 0.5) * 0.02;

    result.push(Math.max(-1, Math.min(1, value)));
  }
  return result;
}

// ── Dynamic Mock Hook ──

export function useWearableMockData(): WearableHealthData {
  const [data, setData] = useState<WearableHealthData>(() => ({
    deviceName: 'Xiaomi Watch S5',
    online: true,
    heartRate: 72,
    ecgWaveform: generateEcgWaveform(),
    bloodPressureSystolic: 118,
    bloodPressureDiastolic: 76,
    spo2: 98,
    sleepHours: 7.2,
    steps: 8547,
    battery: 82,
    lastUpdated: new Date().toISOString(),
  }));

  useEffect(() => {
    const tick = () => {
      setData(prev => {
        // 心率随机波动 ±3
        const hr = Math.round(Math.max(55, Math.min(130, prev.heartRate + (Math.random() - 0.5) * 4)));

        // 血压波动 ±5
        const sys = Math.round(Math.max(90, Math.min(160, prev.bloodPressureSystolic + (Math.random() - 0.5) * 6)));
        const dia = Math.round(Math.max(60, Math.min(100, prev.bloodPressureDiastolic + (Math.random() - 0.5) * 4)));

        // 血氧稳定在 95-99%，仅微小波动
        const sp = Math.round(Math.max(94, Math.min(99, prev.spo2 + (Math.random() - 0.5) * 0.4)));

        // 步数缓慢增长
        const newSteps = prev.steps + Math.floor(Math.random() * 3);

        // 电量模拟 2 天待机：172800秒掉100%，每3秒掉 100/(172800/3) ≈ 0.0017%
        const bat = Math.max(1, +(prev.battery - 0.0017).toFixed(1));

        return {
          ...prev,
          heartRate: hr,
          ecgWaveform: generateEcgWaveform(),
          bloodPressureSystolic: sys,
          bloodPressureDiastolic: dia,
          spo2: sp,
          steps: newSteps,
          battery: bat,
          lastUpdated: new Date().toISOString(),
        };
      });
    };

    const id = setInterval(tick, 3000);
    return () => clearInterval(id);
  }, []);

  return data;
}
